/**
 * @file billingNav.ts
 * @brief Shared billing page path + tab anchors for nav links.
 *
 * @author DES
 * @date 06-07-2026
 * @updated 30-07-2026 — hash tabs (one section visible at a time)
 */

import {
  Bell,
  CreditCard,
  Cpu,
  DollarSign,
  RefreshCw,
  Tags,
  type LucideIcon,
} from "lucide-react";

export const BILLING_PATH = "/admin/billing";

export const BILLING_SECTIONS = {
  customerRates: "customer-rates",
  subscriptionPlans: "subscription-plans",
  costAlerts: "cost-alerts",
  serviceCosts: "service-costs",
  recompute: "recompute",
} as const;

export type BillingSectionId =
  (typeof BILLING_SECTIONS)[keyof typeof BILLING_SECTIONS];

/** Page tab: overview landing or a single section. */
export type BillingTabId = "overview" | BillingSectionId;

export type BillingNavZone = "pricing" | "plans" | "internals";

export function billingSectionHref(section: BillingSectionId): string {
  return `${BILLING_PATH}#${section}`;
}

export function billingTabHref(tab: BillingTabId): string {
  return tab === "overview" ? BILLING_PATH : billingSectionHref(tab);
}

export interface BillingSectionNavItem {
  id: BillingSectionId;
  label: string;
  zone: BillingNavZone;
}

export const BILLING_SECTION_NAV: BillingSectionNavItem[] = [
  {
    id: BILLING_SECTIONS.customerRates,
    label: "Customer rates",
    zone: "pricing",
  },
  {
    id: BILLING_SECTIONS.subscriptionPlans,
    label: "Plans & seats",
    zone: "plans",
  },
  { id: BILLING_SECTIONS.costAlerts, label: "Cost alerts", zone: "internals" },
  {
    id: BILLING_SECTIONS.serviceCosts,
    label: "Service costs",
    zone: "internals",
  },
  { id: BILLING_SECTIONS.recompute, label: "Recompute", zone: "internals" },
];

export const BILLING_ZONE_LABELS: Record<BillingNavZone, string> = {
  pricing: "Pricing",
  plans: "Plans",
  internals: "Internals",
};

const SECTION_IDS = new Set<string>(
  BILLING_SECTION_NAV.map((section) => section.id),
);

export function resolveBillingTab(hash: string): BillingTabId {
  const id = hash.replace(/^#/, "");
  if (!id || id === "top" || id === "overview" || id === "billing-top") {
    return "overview";
  }
  if (SECTION_IDS.has(id)) {
    return id as BillingSectionId;
  }
  return "overview";
}

export function billingSectionLabel(sectionId: string): string | null {
  return (
    BILLING_SECTION_NAV.find((section) => section.id === sectionId)?.label ??
    null
  );
}

// ----------------------------------------------------------------------
// Tab glyphs
// Moved here from BillingSectionTabs.tsx (02-08-2026): the tab strip and the
// overview quick-jump cells both need them, and a value export beside a
// component costs that module its Fast Refresh. This file has no component in
// it, so it is the right home for both the map and its accessor.
// ----------------------------------------------------------------------
export const BILLING_TAB_ICONS: Record<BillingTabId, LucideIcon> = {
  overview: DollarSign,
  "customer-rates": Tags,
  "subscription-plans": CreditCard,
  "cost-alerts": Bell,
  "service-costs": Cpu,
  recompute: RefreshCw,
};

/** Narrow helper for overview quick-jump cells. */
export function billingSectionIcon(id: BillingSectionId): LucideIcon {
  return BILLING_TAB_ICONS[id];
}
